import { geoApi } from '@/features/api/geo'
import { useLicence } from '@/features/licence/hooks/useLicence'
import { AddressSuggestionFiltersQuery } from '@/features/pos/components/CustomerProfile/queries'
import { AddressSuggestion } from '@/features/pos/components/CustomerProfile/types'
import { transformAddressSuggestion } from '@/features/pos/components/CustomerProfile/utils/transforms'
import { useHandleQueryErrorWithToast } from '@/features/pos/hooks/useHandleQueryErrorWithToast'
import { logger } from '@/features/pos/logger'
import { useHandleErrorWithToast } from '@/hooks/useHandleErrorWithToast'
import { useQuery } from '@apollo/client'
import { useEffect, useMemo, useState } from 'react'

export const useAddressSuggestions = (address: string, disabled: boolean): AddressSuggestion[] => {
    const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([])

    const { country } = useLicence()
    const { data, loading, error } = useQuery(AddressSuggestionFiltersQuery)
    const handleError = useHandleErrorWithToast(logger)
    const filters = useMemo(
        () => data?.addressSuggestionFilters.map(({ geoApiFilter }) => geoApiFilter) ?? [],
        [data?.addressSuggestionFilters]
    )

    useHandleQueryErrorWithToast(error, loading)

    const hasEnoughCharacters = address.length >= 3

    useEffect(() => {
        if (disabled || !hasEnoughCharacters) {
            setAddressSuggestions([])
            return
        }

        let cancelled = false

        const handleSet = async () => {
            try {
                const addressSuggestions = await geoApi.getAddressSuggestions(
                    address,
                    country,
                    filters
                )

                if (!cancelled) {
                    setAddressSuggestions(addressSuggestions.map(transformAddressSuggestion))
                }
            } catch (error) {
                if (!cancelled) void handleError(error)
            }
        }

        void handleSet()

        return () => {
            cancelled = true
        }
    }, [address, country, disabled, filters, handleError, hasEnoughCharacters])

    return addressSuggestions
}
